



























































































import { defineComponent, ref, computed, useContext } from '@nuxtjs/composition-api';
import { useLicensePlateFinder } from '../composables/useLicensePlateFinder';
import { SfModal, SfHeading, SfInput, SfButton, SfProperty } from '@storefront-ui/vue';
import WmSelect from 'components/theme/Form/Select.vue';
import { usePartFinderStore } from '../stores/partFinder';
import { storeToRefs } from 'pinia';

export default defineComponent({
  name: 'LicensePlateRegistrationModal',
  components: {
    SfModal,
    SfHeading,
    SfButton,
    SfInput,
    SfProperty,
    WmSelect
  },
  emits: ['close', 'update:information'],
  setup (props, { emit }) {
    const licensePlate = ref('');
    const errorMessage = ref('');
    const { loading, search } = useLicensePlateFinder();
    const result = ref(null);
    
    const store = usePartFinderStore();
    const { finders } = storeToRefs(store);
    
    const selectedBrandId = ref<string | null>(null);
    const selectedModelId = ref<string | null>(null);

    const hasSuggestions = computed(() => {
      return result.value?.suggestions?.length > 0;
    });

    const brandConfirmed = computed(() => result.value?.brand_exists ?? true);
    const modelConfirmed = computed(() => result.value?.model_exists ?? true);

    const brandSuggestions = computed(() => {
      return result.value?.suggestions
        ?.filter(s => s.brand_finder_id === 1 && s.brand && !brandConfirmed.value)
        .map(s => ({
          ...s,
          finder_name: finders.value?.find(f => f.finder_id === s.brand_finder_id)?.name
        })) || [];
    });

    const modelSuggestions = computed(() => {
      if (!brandConfirmed.value && !selectedBrandId.value) return [];

      const brandEntry = result.value?.suggestions?.find(s =>
        s.brand_finder_id === 1 && (brandConfirmed.value || Number(selectedBrandId.value) === s.brand_id)
      );

      return (brandEntry?.models || []).map(m => ({
        ...m,
        finder_name: finders.value?.find(f => f.finder_id === brandEntry.brand_finder_id)?.name
      }));
    });

    const brandSelected = computed(() =>
      brandConfirmed.value || selectedBrandId.value !== null
    );

    const canSave = computed(() => {
      return result.value?.brand && 
        (brandConfirmed.value || selectedBrandId.value) &&
        (modelConfirmed.value || selectedModelId.value);
    });

    const { i18n } = useContext();
    
    async function searchForLicensePlate() {
      errorMessage.value = '';
      result.value = await search(licensePlate.value.replace(/\s/g, ''));

      if (!result.value) {
        errorMessage.value = i18n.t('We were not able to find your tractor');
        return;
      }

      // Block if brand/model exist but from non-finderId 1 and no suggestions
      const isBrandValid = result.value.brand_exists;
      const isModelValid = result.value.model_exists;
      const brandFinderId = result.value.brand_finder_id;
      const suggestions = result.value.suggestions || [];
      const filteredSuggestions = suggestions.filter(s => s.brand_finder_id === 1);

      // Filter suggestions to only include entries from finderId 1
      result.value.suggestions = filteredSuggestions;

      if (brandFinderId !== 1 && filteredSuggestions.length === 0) {
        errorMessage.value = i18n.t('We were not able to verify this license plate.');
        result.value = null;
        return;
      }

      // Auto-select brand if only one suggestion remains
      if (!brandConfirmed.value && brandSuggestions.value.length === 1) {
        const suggestion = brandSuggestions.value[0];
        selectedBrandId.value = String(suggestion.brand_id);
        result.value.brand = suggestion.brand;
        result.value.brand_finder_id = suggestion.brand_finder_id;
      }

      // Auto-select model if only one model remains
      if (!modelConfirmed.value && modelSuggestions.value.length === 1) {
        const suggestion = modelSuggestions.value[0];
        selectedModelId.value = String(suggestion.model_id);
        result.value.model = suggestion.model;
      }
    }

    function updateInformation() {
      if (selectedBrandId.value && !brandConfirmed.value) {
        const selected = result.value.suggestions.find(s => s.brand_id === Number(selectedBrandId.value));
        if (selected) {
          result.value.brand = selected.brand;
          result.value.brand_finder_id = selected.brand_finder_id;
        }
      }

      if (selectedModelId.value && !modelConfirmed.value) {
        const brandEntry = result.value?.suggestions.find(s =>
          brandConfirmed.value || Number(selectedBrandId.value) === s.brand_id
        );

        const selected = brandEntry?.models?.find(
          m => m.model_id === Number(selectedModelId.value)
        );

        if (selected) {
          result.value.model = selected.model;
        }
      }

      emit('update:information', {
        information: result.value,
        brand: result.value.brand,
        model: result.value.model,
        licensePlate: licensePlate.value
      });
      emit('close');
    }

    const showNoModelsError = computed(() => {
      return brandSelected.value && 
             !modelConfirmed.value && 
             modelSuggestions.value.length === 0;
    });

    return {
      licensePlate,
      errorMessage,
      loading,
      result,
      updateInformation,
      searchForLicensePlate,
      hasSuggestions,
      brandConfirmed,
      modelConfirmed,
      selectedBrandId,
      selectedModelId,
      brandSuggestions,
      modelSuggestions,
      brandSelected,
      canSave,
      showNoModelsError
    }
  }
});
